import React, { useState, useEffect, useCallback } from 'react';
import { galleryAPI, GalleryImage, GalleryImagesResponse } from '../../services/galleryApi';
import toast from 'react-hot-toast';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import TypeSelector from '../../components/gallery/TypeSelector';
import SubTypeSelector from '../../components/gallery/SubTypeSelector';
import ImageTile from '../../components/gallery/ImageTile';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';

const GalleryViewer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>(); // Get serviceId from URL params
  const { user } = useAuth(); // Get user from AuthContext

  const [imagesResponse, setImagesResponse] = useState<GalleryImagesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states, initialized from URL query params or path param
  const queryParams = new URLSearchParams(location.search);
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(serviceId || queryParams.get('serviceType') || null);
  const [selectedSubTypeId, setSelectedSubTypeId] = useState<string | null>(queryParams.get('serviceSubType') || null);
  const [searchQuery, setSearchQuery] = useState<string>(queryParams.get('q') || '');
  const [currentPage, setCurrentPage] = useState<number>(parseInt(queryParams.get('page') || '1', 10));
  const limit = 24; // Images per page

  const canDelete = !!user && (user.role === 'admin' || user.role === 'technician');

  const fetchImages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: { [key: string]: any } = {
        page: currentPage,
        limit: limit,
      };

      // If a serviceId is provided in the URL, use it as the initial filter
      if (serviceId) {
        params.serviceType = serviceId;
        params.serviceSubType = serviceId; // Try matching both type and sub-type
      }

      // If filters are selected via dropdowns, they override the URL param
      if (selectedTypeId) {
        params.serviceType = selectedTypeId;
        delete params.serviceSubType; // Clear sub-type if a new type is selected
      }
      if (selectedSubTypeId) {
        params.serviceSubType = selectedSubTypeId;
        // Ensure serviceType is also set if subType is selected, for proper filtering
        // This assumes subType selection implies its parent type is also selected
        if (!params.serviceType && selectedTypeId) {
          params.serviceType = selectedTypeId;
        }
      }

      if (searchQuery) params.q = searchQuery;

      const response = await galleryAPI.getGalleryImages(params);
      setImagesResponse(response);
    } catch (err) {
      console.error('Failed to fetch gallery images:', err);
      setError('Erreur lors du chargement des images de la galerie.');
      toast.error('Erreur lors du chargement des images.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, serviceId, selectedTypeId, selectedSubTypeId, searchQuery]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  // Update URL query params when filters or page change
  useEffect(() => {
    const params = new URLSearchParams();
    // Only add selectedTypeId/SubTypeId to query params if they are different from the initial serviceId
    // This prevents redundant query params when navigating from ServicesGallery
    if (selectedTypeId && selectedTypeId !== serviceId) params.set('serviceType', selectedTypeId);
    if (selectedSubTypeId) params.set('serviceSubType', selectedSubTypeId);
    if (searchQuery) params.set('q', searchQuery);
    if (currentPage !== 1) params.set('page', currentPage.toString());
    navigate({ search: params.toString() }, { replace: true });
  }, [selectedTypeId, selectedSubTypeId, searchQuery, currentPage, navigate, serviceId]);

  const handleTypeChange = (typeId: string) => {
    setSelectedTypeId(typeId);
    setSelectedSubTypeId(null); // Reset sub-type when type changes
    setSearchQuery(''); // Reset search query
    setCurrentPage(1);
    // If a type is selected, navigate to a clean URL without serviceId path param
    if (typeId) {
      navigate(`/technician/gallery-viewer?serviceType=${typeId}`, { replace: true });
    } else {
      navigate(`/technician/gallery-viewer`, { replace: true });
    }
  };

  const handleSubTypeChange = (subTypeId: string) => {
    setSelectedSubTypeId(subTypeId);
    setSearchQuery(''); // Reset search query
    setCurrentPage(1);
    // If a sub-type is selected, navigate to a clean URL without serviceId path param
    if (subTypeId) {
      navigate(`/technician/gallery-viewer?serviceSubType=${subTypeId}`, { replace: true });
    } else if (selectedTypeId) {
      navigate(`/technician/gallery-viewer?serviceType=${selectedTypeId}`, { replace: true });
    } else {
      navigate(`/technician/gallery-viewer`, { replace: true });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleImageDeleteSuccess = (deletedImageId: string) => {
    setImagesResponse(prev => {
      if (!prev) return null;
      return {
        ...prev,
        images: prev.images.filter(img => img._id !== deletedImageId),
        totalDocs: prev.totalDocs - 1,
      };
    });
  };

  return (
    <Layout>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-6">Galerie d'images</h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label htmlFor="searchFilter" className="block text-sm font-medium text-gray-700 mb-1">Rechercher par description</label>
            <input
              type="text"
              id="searchFilter"
              className="input input-bordered w-full"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: limit }).map((_, index) => (
              <div key={index} className="aspect-w-1 aspect-h-1 w-full rounded-lg bg-gray-200 animate-pulse"></div>
            ))}
          </div>
        ) : error ? (
          <div className="text-error text-center py-10">{error}</div>
        ) : imagesResponse?.images.length === 0 ? (
          <div className="text-center py-10 text-gray-500">Aucune image trouvée avec les filtres actuels.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {imagesResponse?.images.map((image) => (
              <ImageTile
                key={image._id}
                image={image}
                onDeleteSuccess={handleImageDeleteSuccess}
                canDelete={canDelete}
              />
            ))}
          </div>
        )}

        {imagesResponse && imagesResponse.totalPages > 1 && (
          <div className="flex justify-center mt-8">
            <div className="join">
              <button
                className="join-item btn"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={!imagesResponse.hasPrevPage}
              >
                «
              </button>
              {[...Array(imagesResponse.totalPages)].map((_, index) => (
                <button
                  key={index}
                  className={`join-item btn ${currentPage === index + 1 ? 'btn-active' : ''}`}
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </button>
              ))}
              <button
                className="join-item btn"
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={!imagesResponse.hasNextPage}
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default GalleryViewer;
